import React, { useState, useEffect } from 'react';
import { X, User, Calendar, FileText, MessageSquare, Send, Paperclip, CheckCircle, Scale, Play, ExternalLink, Download } from 'lucide-react';
import DisputeStatusBadge from './DisputeStatusBadge';
import { disputeService } from '../../../services/disputeService';
import { supabase } from '../../../lib/supabase';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr)?.toLocaleString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const DisputeDetailPanel = ({ dispute, onClose, onStatusChange }) => {
  const [adminNotes, setAdminNotes] = useState(dispute?.adminNotes || '');
  const [resolutionText, setResolutionText] = useState(dispute?.resolutionDetails || '');
  const [evidence, setEvidence] = useState([]);
  const [loadingEvidence, setLoadingEvidence] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [showResolutionInput, setShowResolutionInput] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    if (dispute?.id) {
      loadEvidence();
      loadMessages();
      setAdminNotes(dispute?.adminNotes || '');
      setResolutionText(dispute?.resolutionDetails || '');
    }
  }, [dispute?.id]);

  const loadEvidence = async () => {
    setLoadingEvidence(true);
    try {
      const data = await disputeService?.getDisputeEvidence(dispute?.id);
      setEvidence(data || []);
    } catch (err) {
      console.error('Error loading evidence:', err);
    } finally {
      setLoadingEvidence(false);
    }
  };

  const loadMessages = async () => {
    try {
      const { data, error } = await supabase?.from('dispute_messages')?.select(`*, sender:user_profiles!dispute_messages_sender_id_fkey(id, full_name, email, avatar_url)`)?.eq('dispute_id', dispute?.id)?.order('created_at', { ascending: true });
      if (!error && data) setMessages(data);
    } catch (err) {
      // dispute_messages table may not exist yet
      console.warn('Messages not available:', err);
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await supabase?.from('disputes')?.update({ admin_notes: adminNotes, updated_at: new Date()?.toISOString() })?.eq('id', dispute?.id);
    } catch (err) {
      console.error('Error saving notes:', err);
    } finally {
      setSavingNotes(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage?.trim()) return;
    setSendingMessage(true);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      await supabase?.from('dispute_messages')?.insert({
        dispute_id: dispute?.id,
        sender_id: user?.id,
        message: newMessage?.trim(),
        created_at: new Date()?.toISOString()
      });
      setNewMessage('');
      await loadMessages();
    } catch (err) {
      console.warn('Could not send message:', err);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleStartMediation = async () => {
    setActionLoading('start');
    try {
      await disputeService?.updateDisputeStatus(dispute?.id, 'in_mediation', adminNotes);
      const complainant = dispute?.filedByProfile;
      const respondent = dispute?.respondentProfile;
      const subject = encodeURIComponent(`Spor #${dispute?.id?.slice(0,8)} - Pokrenuta medijacija`);
      const body = encodeURIComponent(
        `Poštovani,\n\nObaveštavamo Vas da je za spor "${dispute?.title}" pokrenuta medijacija.\n\nAdmin platforma ValueBook će pokušati da pomogne u sporazumnom rešavanju.\n\nPozdrav,\nAdmin tim ValueBook`);
      const to = encodeURIComponent(`${complainant?.email || ''},${respondent?.email || ''}`);
      window.open(`https://mail.google.com/mail/?view=cm&to=${to}&su=${subject}&body=${body}`, '_blank');
      onStatusChange();
    } catch (err) {
      console.error('Error starting mediation:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleProposeResolution = async () => {
    if (!resolutionText?.trim()) { setShowResolutionInput(true); return; }
    setActionLoading('propose');
    try {
      await supabase?.from('disputes')?.update({ resolution_details: resolutionText, updated_at: new Date()?.toISOString() })?.eq('id', dispute?.id);
      const subject = encodeURIComponent(`Spor #${dispute?.id?.slice(0,8)} - Predlog rešenja`);
      const body = encodeURIComponent(
        `Poštovani,\n\nAdmin platforma ValueBook predlaže sledeće rešenje za spor "${dispute?.title}":\n\n${resolutionText}\n\nMolimo Vas da se izjasnite o predlogu.\n\nPozdrav,\nAdmin tim ValueBook`);
      const to = encodeURIComponent(`${dispute?.filedByProfile?.email || ''},${dispute?.respondentProfile?.email || ''}`);
      window.open(`https://mail.google.com/mail/?view=cm&to=${to}&su=${subject}&body=${body}`, '_blank');
      onStatusChange();
    } catch (err) {
      console.error('Error proposing resolution:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkResolved = async () => {
    setActionLoading('resolve');
    try {
      await disputeService?.resolveDispute(dispute?.id, 'mediated', resolutionText || adminNotes);
      onStatusChange();
    } catch (err) {
      console.error('Error resolving dispute:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleEscalate = async () => {
    setActionLoading('escalate');
    try {
      await disputeService?.escalateDispute(dispute?.id, adminNotes || 'Eskalacija na spoljno rešavanje');
      const subject = encodeURIComponent(`Spor #${dispute?.id?.slice(0,8)} - Upućivanje na spoljno rešavanje`);
      const body = encodeURIComponent(
        `Poštovani,\n\nNažalost, medijacija za spor "${dispute?.title}" nije dala rezultate.\n\nPlatforma ValueBook nije učesnik transakcija između korisnika i ne može biti strana u sporovima. Medijacija se pruža isključivo kao tehnička pomoć u komunikaciji.\n\nUpućujemo Vas na spoljno rešavanje spora:\n• Nadležni sud prema mestu stanovanja\n• Arbitražni sud\n• Alternativno rešavanje sporova (ARS)\n\nPozdrav,\nAdmin tim ValueBook`);
      const to = encodeURIComponent(`${dispute?.filedByProfile?.email || ''},${dispute?.respondentProfile?.email || ''}`);
      window.open(`https://mail.google.com/mail/?view=cm&to=${to}&su=${subject}&body=${body}`, '_blank');
      onStatusChange();
    } catch (err) {
      console.error('Error escalating dispute:', err);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex items-start justify-between p-5 border-b border-gray-200 bg-gray-50 flex-shrink-0">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-xs text-gray-400">#{dispute?.id?.slice(0, 8)}</span>
            <DisputeStatusBadge status={dispute?.disputeStatus} />
          </div>
          <h2 className="text-lg font-bold text-gray-900 truncate">{dispute?.title || 'Bez naslova'}</h2>
          <p className="text-xs text-gray-500 mt-0.5 capitalize">{dispute?.disputeCategory || 'Opšti spor'}</p>
        </div>
        <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-200 text-gray-500 transition-colors ml-3 flex-shrink-0">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {/* Parties */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
            <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-2">Podnosilac</p>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-blue-200 flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 text-sm truncate">{dispute?.filedByProfile?.full_name || '—'}</p>
                <p className="text-xs text-gray-500 truncate">{dispute?.filedByProfile?.email || '—'}</p>
              </div>
            </div>
          </div>
          <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
            <p className="text-xs font-semibold text-orange-600 uppercase tracking-wide mb-2">Tuženi</p>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-orange-200 flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-orange-600" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 text-sm truncate">{dispute?.respondentProfile?.full_name || '—'}</p>
                <p className="text-xs text-gray-500 truncate">{dispute?.respondentProfile?.email || '—'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Dates */}
        <div className="flex gap-4 text-xs text-gray-500">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            <span>Prijavljeno: <strong className="text-gray-700">{formatDate(dispute?.filedAt)}</strong></span>
          </div>
          {dispute?.resolvedAt && (
            <div className="flex items-center gap-1.5">
              <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
              <span>Rešeno: <strong className="text-gray-700">{formatDate(dispute?.resolvedAt)}</strong></span>
            </div>
          )}
        </div>

        {/* Description */}
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5" /> Opis spora
          </p>
          <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{dispute?.description || 'Nema opisa.'}</p>
          {dispute?.desiredResolution && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-xs font-semibold text-gray-500 mb-1">Željeno rešenje (podnosilac):</p>
              <p className="text-sm text-gray-700 italic">{dispute?.desiredResolution}</p>
            </div>
          )}
        </div>

        {/* Evidence */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
            <Paperclip className="w-3.5 h-3.5" /> Dokazi ({evidence?.length || 0})
          </p>
          {loadingEvidence ? (
            <p className="text-xs text-gray-400">Učitavanje dokaza...</p>
          ) : evidence?.length === 0 ? (
            <p className="text-xs text-gray-400 italic">Nema priloženih dokaza</p>
          ) : (
            <div className="space-y-2">
              {evidence?.map((ev) => (
                <div key={ev?.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <Paperclip className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{ev?.fileName}</p>
                    {ev?.description && <p className="text-xs text-gray-500">{ev?.description}</p>}
                  </div>
                  {ev?.signedUrl && (
                    <a href={ev?.signedUrl} target="_blank" rel="noopener noreferrer"
                      className="p-1.5 rounded hover:bg-blue-100 text-blue-600 transition-colors flex-shrink-0">
                      <Download className="w-4 h-4" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Admin Notes */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
            <MessageSquare className="w-3.5 h-3.5" /> Interne admin beleške
          </p>
          <textarea
            value={adminNotes}
            onChange={(e) => setAdminNotes(e?.target?.value)}
            placeholder="Beleške vidljive samo adminu..."
            rows={3}
            className="w-full text-sm border border-gray-200 rounded-xl p-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
          />
          <button
            onClick={saveNotes}
            disabled={savingNotes}
            className="mt-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
          >
            {savingNotes ? 'Čuvanje...' : 'Sačuvaj beleške'}
          </button>
        </div>

        {/* Resolution Proposal */}
        {(showResolutionInput || dispute?.resolutionDetails) && (
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Predlog rešenja</p>
            <textarea
              value={resolutionText}
              onChange={(e) => setResolutionText(e?.target?.value)}
              placeholder="Unesite predlog rešenja koji će biti poslat obe strane..."
              rows={4}
              className="w-full text-sm border border-amber-200 rounded-xl p-3 resize-none focus:outline-none focus:ring-2 focus:ring-amber-400 bg-amber-50"
            />
          </div>
        )}

        {/* Communication Thread */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
            <MessageSquare className="w-3.5 h-3.5" /> Komunikacija
          </p>
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="max-h-48 overflow-y-auto p-3 space-y-2 bg-gray-50">
              {messages?.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">Nema poruka</p>
              ) : (
                messages?.map((msg) => (
                  <div key={msg?.id} className="bg-white rounded-lg p-2.5 border border-gray-100">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-xs font-semibold text-gray-700">{msg?.sender?.full_name || msg?.sender?.email || 'Admin'}</span>
                      <span className="text-xs text-gray-400">{formatDate(msg?.created_at)}</span>
                    </div>
                    <p className="text-sm text-gray-800">{msg?.message}</p>
                  </div>
                ))
              )}
            </div>
            <div className="flex gap-2 p-3 border-t border-gray-200 bg-white">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e?.target?.value)}
                onKeyDown={(e) => e?.key === 'Enter' && !e?.shiftKey && sendMessage()}
                placeholder="Poruka stranama u sporu..."
                className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={sendMessage}
                disabled={sendingMessage || !newMessage?.trim()}
                className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={handleStartMediation}
            disabled={actionLoading === 'start' || dispute?.disputeStatus === 'in_mediation'}
            className="flex items-center justify-center gap-2 px-3 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Play className="w-4 h-4" />
            {actionLoading === 'start' ? 'Pokretanje...' : 'Započni medijaciju'}
          </button>
          <button
            onClick={() => { setShowResolutionInput(true); handleProposeResolution(); }}
            disabled={actionLoading === 'propose'}
            className="flex items-center justify-center gap-2 px-3 py-2.5 bg-amber-500 text-white rounded-xl text-sm font-semibold hover:bg-amber-600 disabled:opacity-50 transition-colors"
          >
            <Scale className="w-4 h-4" />
            {actionLoading === 'propose' ? 'Slanje...' : 'Predloži rešenje'}
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={handleMarkResolved}
            disabled={actionLoading === 'resolve' || dispute?.disputeStatus === 'resolved'}
            className="flex items-center justify-center gap-2 px-3 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 transition-colors"
          >
            <CheckCircle className="w-4 h-4" />
            {actionLoading === 'resolve' ? 'Zatvaranje...' : 'Označi kao rešen'}
          </button>
          <button
            onClick={handleEscalate}
            disabled={actionLoading === 'escalate' || dispute?.disputeStatus === 'escalated'}
            className="flex items-center justify-center gap-2 px-3 py-2.5 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            {actionLoading === 'escalate' ? 'Eskalacija...' : 'Eskaluj na spoljno'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DisputeDetailPanel;
